import { Icon } from '@iconify/react';
import { useCallback, useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import searchFill from '@iconify/icons-eva/search-fill';
import { useDispatch } from 'react-redux';
import roundFilterList from '@iconify/icons-ic/round-filter-list';
import { MdUpdate } from 'react-icons/md';
import add from '@iconify/icons-ic/add';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import { BiCloudDownload } from 'react-icons/bi';
import { DateRangeStyled, RootStyle, SearchStyle } from '../Global/Styling';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { fDate } from '../../utils/formatTime';
import {
  getCityData,
  getLoanTypeData,
  getSourceListData,
  getStatusList
} from '../../store/Slice/Global/GlobalSlice';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';

export default function BankopsToolbar({
  filterName,
  onFilterName,
  filterDatesRange,
  setFilterDatesRange,
  filterState,
  setFilterState,
  parent,
  selected,
  setFilterName,
  handleDownload
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isOpenFilter, openFilter] = useState(false);
  const { LoginData } = useLoginSelector();
  const { CityDrop, LoanTypeDrop, StatusDrop, SourceDrop } = useGlobalSelector();

  const handleRemoveFilter = useCallback(() => {
    setFilterDatesRange([{ startDate: null, endDate: null, key: 'selection' }]);
    setFilterState({
      product: ' ',
      productId: null,
      city: null,
      cityId: null,
      status: ' ',
      statusId: null,
      employmentType: ' '
    });
    setFilterName('');
  }, [setFilterDatesRange, setFilterState, setFilterName]);

  useEffect(() => {
    const commonObj = { companyId: LoginData?.companyId, userId: LoginData?.userId };
    dispatch(getLoanTypeData(commonObj));
    dispatch(getCityData({ ...commonObj, searchLiteral: '', sortOrder: 'desc' }));
    dispatch(getStatusList(58));
    dispatch(getSourceListData());
  }, [dispatch, LoginData]);

  const updateFilter = useCallback(
    (key, value, extra = {}) => {
      setFilterState((prev) => ({ ...prev, [key]: value, ...extra }));
    },
    [setFilterState]
  );

  return (
    <>
      {isOpenFilter && (
        <Box px={3} pt={2}>
          <Grid container spacing={3}>
            <Grid item xs>
              <Select
                fullWidth
                value={filterState.product}
                onChange={(e) => {
                  const val = e.target.value;
                  const parsed = val !== ' ' ? JSON.parse(val) : {};
                  updateFilter('product', val, { productId: parsed.loanId });
                }}
                inputProps={{
                  className: `textInput customSelectBox dissabledMenu${filterState.product}`
                }}
              >
                <MenuItem value=" " disabled>
                  Select Product
                </MenuItem>
                {LoanTypeDrop?.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs sx={{ position: 'relative' }}>
              <Box className="displayDateRange">
                {filterDatesRange[0].startDate ? `${fDate(filterDatesRange[0].startDate)} to ` : ''}
                {filterDatesRange[0].endDate ? fDate(filterDatesRange[0].endDate) : ''}
                {!filterDatesRange[0].startDate && !filterDatesRange[0].endDate && (
                  <Typography className="dissabledMenu">Select Date Range</Typography>
                )}
              </Box>
              <Select
                fullWidth
                inputProps={{ className: 'textInput' }}
                MenuProps={{ className: 'menuOpened' }}
                value=" "
                className="datesSelect"
              >
                <DateRangeStyled
                  editableDateInputs={false}
                  onChange={(item) => setFilterDatesRange([item.selection])}
                  moveRangeOnFirstSelection={false}
                  ranges={filterDatesRange}
                  className="dateRangePicker"
                />
              </Select>
            </Grid>
            <Grid item xs>
              <Autocomplete
                size="small"
                options={CityDrop || []}
                getOptionLabel={(option) => option.label || ''}
                value={filterState.city || null}
                renderInput={(params) => (
                  <TextField {...params} fullWidth placeholder="Select City" />
                )}
                onChange={(_, newValue) =>
                  updateFilter('city', newValue, { cityId: newValue?.value || null })
                }
              />
            </Grid>
            <Grid item xs>
              <Select
                fullWidth
                value={filterState.status}
                onChange={(e) => {
                  const val = e.target.value;
                  const parsed = val !== ' ' ? JSON.parse(val) : {};
                  updateFilter('status', val, { statusId: parsed.stageStatusId });
                }}
                inputProps={{
                  className: `textInput customSelectBox dissabledMenu${filterState.status}`
                }}
              >
                <MenuItem value=" " disabled>
                  Select Status
                </MenuItem>
                {StatusDrop?.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs sx={{ display: 'none' }}>
              <Select
                fullWidth
                value={filterState.employmentType}
                onChange={(e) => {
                  const val = e.target.value;
                  const parsed = val !== ' ' ? JSON.parse(val) : {};
                  updateFilter('employmentType', val, { employmentTypeId: parsed.typeId });
                }}
                inputProps={{
                  className: `textInput customSelectBox dissabledMenu${filterState.employmentType}`
                }}
              >
                <MenuItem value=" " disabled>
                  Select Source
                </MenuItem>
                {SourceDrop?.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs="auto">
              <Tooltip title="Reset Filter">
                <IconButton sx={{ py: 0.8 }} onClick={handleRemoveFilter}>
                  <FilterListOffIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>
      )}

      <RootStyle
        sx={{
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          p: { xs: 2, sm: 2.5 },
          bgcolor: '#ffffff'
        }}
      >
        <Box display="flex" gap={1.25} flexWrap="wrap" alignItems="center">
          <Button
            variant="contained"
            onClick={() => navigate(`/${parent}/add-login-file`)}
            startIcon={<Icon icon={add} />}
            sx={{
              bgcolor: '#0284c7',
              color: '#ffffff',
              borderRadius: '10px',
              px: 2.2,
              py: 0.9,
              fontWeight: 600,
              fontSize: '13px',
              textTransform: 'none',
              boxShadow: '0 4px 14px rgba(2, 132, 199, 0.25)',
              '&:hover': { bgcolor: '#0369a1' }
            }}
          >
            + Add Login Files
          </Button>

          <Button
            variant="outlined"
            disabled={selected?.length < 1}
            onClick={() => {
              localStorage.setItem('selectedItem', JSON.stringify(selected));
              navigate(`/${parent}/update`);
            }}
            startIcon={<MdUpdate size={18} />}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '13px',
              color: '#475569',
              borderColor: '#e2e8f0',
              bgcolor: '#ffffff',
              px: 1.75,
              py: 0.8,
              '&:hover': {
                borderColor: '#0284c7',
                bgcolor: 'rgba(2, 132, 199, 0.04)',
                color: '#0284c7'
              }
            }}
          >
            Update ({selected?.length || 0})
          </Button>

          <Button
            variant="outlined"
            onClick={() => handleDownload(selected)}
            startIcon={<BiCloudDownload size={18} />}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '13px',
              color: '#475569',
              borderColor: '#e2e8f0',
              bgcolor: '#ffffff',
              px: 1.75,
              py: 0.8,
              '&:hover': {
                borderColor: '#0284c7',
                bgcolor: 'rgba(2, 132, 199, 0.04)',
                color: '#0284c7'
              }
            }}
          >
            Download
          </Button>
        </Box>

        <Box
          display="flex"
          alignItems="center"
          gap={1.5}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          <TextField
            size="small"
            value={filterName}
            onChange={onFilterName}
            placeholder="Search Application..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon icon={searchFill} style={{ color: '#94a3b8', fontSize: 18 }} />
                </InputAdornment>
              )
            }}
            sx={{
              width: { xs: '100%', sm: 260 },
              '& .MuiOutlinedInput-root': {
                bgcolor: '#fff',
                borderRadius: '10px',
                fontSize: '13px',
                '& fieldset': { borderColor: '#e2e8f0' },
                '&:hover fieldset': { borderColor: '#cbd5e1' },
                '&.Mui-focused fieldset': { borderColor: '#0284c7' }
              }
            }}
          />

          <Button
            variant="outlined"
            onClick={() => openFilter(!isOpenFilter)}
            startIcon={<Icon icon={roundFilterList} />}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '13px',
              color: isOpenFilter ? '#0284c7' : '#475569',
              borderColor: isOpenFilter ? '#0284c7' : '#e2e8f0',
              bgcolor: isOpenFilter ? 'rgba(2, 132, 199, 0.06)' : '#fff',
              height: 40,
              px: 2,
              '&:hover': {
                borderColor: '#0284c7',
                bgcolor: 'rgba(2, 132, 199, 0.06)',
                color: '#0284c7'
              }
            }}
          >
            Filters
          </Button>
        </Box>
      </RootStyle>
    </>
  );
}
